import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

/**
 * La recherche profonde.
 *
 * L'ancienne page interrogeait Zoho, et c'est là qu'était le plafond : Zoho ne
 * sait pas chercher sur un intervalle de dates. Elle contournait en lançant une
 * requête par journée — jusqu'à cent vingt pour une plage — sa recherche
 * plafonne à deux cents résultats par page, et il ne garde pas l'historique des
 * étapes. Résultat : un seul champ de date, aucun filtre combinable, et pas
 * d'export.
 *
 * Nos fichiers locaux portent 5 233 passages sur 3 269 dossiers. L'index se
 * construit en 400 ms et chaque recherche répond en une dizaine de
 * millisecondes, filtres combinés compris.
 *
 * L'unité est le WORK ORDER : une commande revient sous la même référence O
 * avec un W neuf, et c'est le W qui est un passage. Le sélecteur en haut des
 * résultats bascule entre une ligne par passage et une ligne par dossier.
 */

const jeton = () => localStorage.getItem('admin_token');

const CHAMPS_DATE = [
  ['intervention', 'Date d’intervention'],
  ['cr', 'Date de soumission du CR'],
  ['w', 'Date d’arrivée du work order'],
  ['cloture', 'Date de clôture'],
  ['etape', 'Dernier changement d’étape'],
];

const VIDE = {
  q: '', champDate: 'intervention', d1: '', d2: '',
  res: [], type: [], typo: [], tech: [], etape: [], cause: [], departement: [], passages: [],
  fpv: '', fpv_cr: false, fpv_decoche: false,
  avec_devis: false, avec_montant: false, avec_dostec: false, hors_sla: false,
};

const RACCOURCIS = [
  ['Ce mois', () => { const d = new Date(); return [mois(d, 0)[0], mois(d, 0)[1]]; }],
  ['Mois dernier', () => mois(new Date(), -1)],
  ['30 derniers jours', () => {
    const f = new Date(); const d = new Date(); d.setDate(d.getDate() - 30);
    return [iso(d), iso(f)];
  }],
  ['Cette année', () => { const a = new Date().getFullYear(); return [`${a}-01-01`, `${a}-12-31`]; }],
];

// Jamais toISOString pour une date locale : minuit a Paris est la veille en
// UTC, et « ce mois » commencait le 30 du mois precedent.
const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  + `-${String(d.getDate()).padStart(2, '0')}`;
function mois(ref, decalage) {
  const d = new Date(ref.getFullYear(), ref.getMonth() + decalage, 1);
  const f = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return [iso(d), iso(f)];
}

const jourFr = v => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '');
const euros = v => (v ? Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 }) + ' €' : '');

// Les recherches enregistrees vivent dans le navigateur : chacun garde les
// siennes, et personne n'a a demander la creation d'un filtre.
function ageTexte(iso) {
  if (!iso) return 'jamais';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 90) return 'à l’instant';
  const m = Math.round(s / 60);
  if (m < 60) return 'il y a ' + m + ' min';
  const h = Math.round(m / 60);
  if (h < 24) return 'il y a ' + h + ' h';
  return 'le ' + new Date(iso).toLocaleDateString('fr-FR');
}

const CLE_ENREG = 'recherche_enregistrees';
function lireEnregistrees() {
  try {
    const v = JSON.parse(localStorage.getItem(CLE_ENREG));
    return Array.isArray(v) ? v : DEFAUT_ENREG;
  } catch { return DEFAUT_ENREG; }
}
const DEFAUT_ENREG = [
  { nom: 'CLEARED du mois', c: { res: ['CLEARED'], champDate: 'cr', ...(() => {
    const [a, b] = mois(new Date(), 0); return { d1: a, d2: b };
  })() } },
  { nom: 'FPV du mois', c: { fpv: 'true', champDate: 'cr', ...(() => {
    const [a, b] = mois(new Date(), 0); return { d1: a, d2: b };
  })() } },
  { nom: 'Dossiers qui résistent', c: { passages: ['3'], granularite: 'dossier' } },
];

// ─── Petits composants ───────────────────────────────────────────────────────
function Bloc({ titre, accent, children }) {
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)',
      borderLeft: `3px solid ${accent || 'var(--border2)'}`, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase',
        padding: '10px 13px 8px', color: accent || 'var(--text2)' }}>{titre}</div>
      <div style={{ padding: '0 13px 13px', display: 'flex', flexDirection: 'column', gap: 7 }}>{children}</div>
    </div>
  );
}

function Coche({ libelle, n, coche, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5 }}>
      <input type="checkbox" checked={coche} onChange={onChange}
        style={{ width: 15, height: 15, accentColor: 'var(--blue2)', flex: 'none' }} />
      <span style={{ color: coche ? 'var(--text)' : 'var(--text2)', overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{libelle}</span>
      {n != null && (
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>
          {n.toLocaleString('fr-FR')}
        </span>
      )}
    </label>
  );
}

/**
 * Un groupe de cases alimenté par les facettes du serveur.
 *
 * Au-delà d'une dizaine de valeurs, une barre filtre la liste elle-même :
 * taper « RACC » pour trouver les quatre typologies de reprise après SAV vaut
 * mieux que dérouler quarante lignes. Les valeurs déjà cochées restent
 * visibles même quand le filtre les exclut, sinon on croit les avoir perdues.
 */
function Facette({ titre, accent, valeurs, choisies, onBasculer, max = 12, vides }) {
  const [tout, setTout] = useState(false);
  const [filtre, setFiltre] = useState('');

  const sansAccents = v => String(v || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const cherche = sansAccents(filtre).trim();

  const retenues = cherche
    ? valeurs.filter(v => sansAccents(v.libelle || v.valeur).includes(cherche) || choisies.includes(v.valeur))
    : valeurs;
  const liste = (tout || cherche) ? retenues : retenues.slice(0, max);
  const longue = valeurs.length > max;

  return (
    <Bloc titre={titre} accent={accent}>
      {longue && (
        <input value={filtre} onChange={e => setFiltre(e.target.value)}
          placeholder={`Filtrer ces ${valeurs.length} valeurs…`}
          style={{ width: '100%', fontSize: 11.5, padding: '5px 8px', borderRadius: 6,
            border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)',
            boxSizing: 'border-box' }} />
      )}
      {valeurs.length === 0 && (
        <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>{vides || 'Aucune valeur.'}</span>
      )}
      {valeurs.length > 0 && liste.length === 0 && (
        <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>Rien ne correspond à « {filtre} ».</span>
      )}
      {liste.map(v => (
        <Coche key={v.valeur} libelle={v.libelle || v.valeur} n={v.n}
          coche={choisies.includes(v.valeur)} onChange={() => onBasculer(v.valeur)} />
      ))}
      {longue && !cherche && retenues.length > max && (
        <button onClick={() => setTout(t => !t)}
          style={{ font: 'inherit', fontSize: 11.5, fontWeight: 600, background: 'none', border: 0,
            color: 'var(--blue2)', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
          {tout ? 'Réduire' : `Voir les ${retenues.length - max} autres`}
        </button>
      )}
    </Bloc>
  );
}

const COLONNES_PASSAGE = [
  ['ref_commande', 'Réf commande'], ['ref_work_order', 'Work order'], ['client', 'Client'],
  ['technicien', 'Technicien'], ['typo', 'Typologie'], ['resultat', 'Résultat'],
  ['date', 'Date'], ['cause', 'Cause'], ['montant', 'Devis'], ['fpv', 'FPV'],
];

/** « VALEA-D3_45 (Jeremy CAPORUSCIO) » : le code est stable, le nom se lit. */
function technicienLisible(l) {
  const code = (l.technicien || '').trim();
  const nom = (l.technicien_nom || '').trim();
  if (code && nom && code !== nom) return { code, nom };
  if (code) return { code, nom: '' };
  return { code: '', nom };
}
const COLONNES_DOSSIER = [
  ['ref_commande', 'Réf commande'], ['client', 'Client'], ['etape', 'Étape'],
  ['typo', 'Typologie'], ['passages', 'Passages'], ['bilan', 'Bilan'],
  ['date', 'Dernière date'], ['cause', 'Dernière cause'], ['montant', 'Devis cumulé'], ['fpv', 'FPV'],
];

export default function Recherche() {
  const [c, setC] = useState(VIDE);
  const [granularite, setGranularite] = useState('passage');
  const [tri, setTri] = useState({ cle: '', sens: 'desc' });
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [enregistrees, setEnregistrees] = useState(lireEnregistrees);
  const [exportEnCours, setExportEnCours] = useState(false);
  const [fpvEnCours, setFpvEnCours] = useState(false);
  const minuteur = useRef(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (c.q) p.set('q', c.q);
    if (c.d1 || c.d2) {
      p.set('champDate', c.champDate);
      if (c.d1) p.set('d1', c.d1);
      if (c.d2) p.set('d2', c.d2);
    }
    for (const k of ['res', 'type', 'typo', 'tech', 'etape', 'cause', 'departement', 'passages']) {
      if (c[k].length) p.set(k, c[k].join(','));
    }
    if (c.fpv) p.set('fpv', c.fpv);
    for (const k of ['fpv_cr', 'fpv_decoche', 'avec_devis', 'avec_montant', 'avec_dostec', 'hors_sla']) {
      if (c[k]) p.set(k, 'true');
    }
    p.set('granularite', granularite);
    if (tri.cle) { p.set('tri', tri.cle); p.set('sens', tri.sens); }
    return p;
  }, [c, granularite, tri]);

  const chercher = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const r = await fetch(`/api/admin/recherche?${params.toString()}`,
        { headers: { Authorization: `Bearer ${jeton()}` } });
      if (!r.ok) throw new Error((await r.json()).error || `Erreur ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErreur(e.message || 'Recherche impossible');
    } finally { setChargement(false); }
  }, [params]);

  // Une frappe au clavier ne doit pas lancer une requête par lettre, mais la
  // recherche doit rester vivante : on laisse passer 350 ms.
  useEffect(() => {
    clearTimeout(minuteur.current);
    minuteur.current = setTimeout(chercher, 350);
    return () => clearTimeout(minuteur.current);
  }, [chercher]);

  const basculer = (cle, valeur) => setC(x => ({
    ...x,
    [cle]: x[cle].includes(valeur) ? x[cle].filter(v => v !== valeur) : [...x[cle], valeur],
  }));

  const f = data?.facettes || {};
  const t = data?.totaux || {};

  const exporter = async () => {
    setExportEnCours(true);
    try {
      const p = new URLSearchParams(params);
      p.set('format', 'csv');
      const r = await fetch(`/api/admin/recherche?${p.toString()}`,
        { headers: { Authorization: `Bearer ${jeton()}` } });
      if (!r.ok) throw new Error('Export impossible');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recherche-valea-${iso(new Date())}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErreur(e.message);
    } finally { setExportEnCours(false); }
  };

  // Vingt secondes et une vingtaine d'appels Zoho : on ne le refait pas a
  // chaque recherche. Le releve se rafraichit seul toutes les douze heures.
  const rafraichirFpv = async () => {
    setFpvEnCours(true);
    try {
      const r = await fetch('/api/admin/recherche/zoho',
        { method: 'POST', headers: { Authorization: `Bearer ${jeton()}` } });
      if (!r.ok) throw new Error((await r.json()).error || 'Relevé impossible');
      await chercher();
    } catch (e) {
      setErreur(e.message);
    } finally { setFpvEnCours(false); }
  };

  const enregistrer = () => {
    const nom = window.prompt('Nom de la recherche ?');
    if (!nom) return;
    const suivant = [...enregistrees, { nom, c: { ...c, granularite } }];
    setEnregistrees(suivant);
    try { localStorage.setItem(CLE_ENREG, JSON.stringify(suivant)); } catch { /* navigation privée */ }
  };

  const rappeler = (r) => {
    setC({ ...VIDE, ...r.c });
    setGranularite(r.c.granularite || 'passage');
  };

  const oublier = (i) => {
    const suivant = enregistrees.filter((_, j) => j !== i);
    setEnregistrees(suivant);
    try { localStorage.setItem(CLE_ENREG, JSON.stringify(suivant)); } catch { /* rien */ }
  };

  // Les filtres actifs, retirables un par un : une recherche dont on ne voit
  // pas les critères donne un résultat qu'on ne sait pas lire.
  const tags = [];
  if (c.q) tags.push([`texte : « ${c.q} »`, () => setC(x => ({ ...x, q: '' }))]);
  if (c.d1 || c.d2) {
    const nom = (CHAMPS_DATE.find(x => x[0] === c.champDate) || [])[1] || '';
    tags.push([`${nom} : ${jourFr(c.d1) || '…'} → ${jourFr(c.d2) || '…'}`,
      () => setC(x => ({ ...x, d1: '', d2: '' }))]);
  }
  for (const k of ['res', 'type', 'typo', 'tech', 'etape', 'cause', 'departement', 'passages']) {
    for (const v of c[k]) {
      const libelle = k === 'passages'
        ? (v === '3' ? '3 passages ou plus' : `${v} passage${v > 1 ? 's' : ''}`)
        : k === 'departement' ? `dépt ${v}` : v;
      tags.push([libelle, () => basculer(k, v)]);
    }
  }
  if (c.fpv === 'true') tags.push(['FPV coché', () => setC(x => ({ ...x, fpv: '' }))]);
  if (c.fpv === 'non') tags.push(['sans FPV', () => setC(x => ({ ...x, fpv: '' }))]);
  for (const [k, lbl] of [['fpv_cr', 'FPV coché au compte rendu'],
    ['fpv_decoche', 'FPV décoché par la CA'], ['avec_devis', 'avec devis'],
    ['avec_montant', 'montant renseigné'], ['avec_dostec', 'avec DOSTEC'],
    ['hors_sla', 'sorti du calcul SLA']]) {
    if (c[k]) tags.push([lbl, () => setC(x => ({ ...x, [k]: false }))]);
  }

  const colonnes = granularite === 'dossier' ? COLONNES_DOSSIER : COLONNES_PASSAGE;
  const champTri = {
    ref_commande: 'ref_commande', client: 'client', technicien: 'technicien', typo: 'typo',
    resultat: 'resultat', etape: 'etape', passages: 'passages', montant: 'montant',
    date: granularite === 'dossier' ? 'date_etape'
      : { intervention: 'date_intervention', cr: 'date_cr', w: 'date_w', cloture: 'date_cloture', etape: 'date_etape' }[c.champDate],
  };

  const dateLigne = l => (granularite === 'dossier'
    ? l.date_etape
    : l[{ intervention: 'date_intervention', cr: 'date_cr', w: 'date_w', cloture: 'date_cloture', etape: 'date_etape' }[c.champDate]]);

  const pastille = (texte, couleur, fond) => (
    <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 7px', borderRadius: 5,
      whiteSpace: 'nowrap', color: couleur, background: fond }}>{texte}</span>
  );

  const cellule = (l, cle) => {
    switch (cle) {
      case 'ref_commande':
        return (
          <>
            <a href={`/dossier/${l.dossier_id}`} target="_blank" rel="noreferrer"
              style={{ fontFamily: 'var(--mono)', fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}>
              {l.ref_commande || '—'}
            </a>
            {granularite === 'dossier' && l.cp && (
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)' }}>{l.cp}</span>
            )}
          </>
        );
      case 'ref_work_order':
        return (
          <>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--text2)' }}>{l.ref_work_order || '—'}</span>
            {l.cp && <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)' }}>{l.cp}</span>}
          </>
        );
      case 'technicien': {
        const { code, nom } = technicienLisible(l);
        if (!code && !nom) return '';
        return (
          <>
            <span style={{ fontFamily: code ? 'var(--mono)' : 'inherit', fontSize: 12 }}>{code || nom}</span>
            {code && nom && (
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)' }}>{nom}</span>
            )}
          </>
        );
      }
      case 'client':
        return <span title={l.client} style={{ display: 'block', maxWidth: 230, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.client || '—'}</span>;
      case 'resultat':
        // L'issue vient de l'étape, pas du compte rendu : « sans CR » annonçait
        // une absence là où la fiche montrait un compte rendu, faute d'un
        // rattachement par référence W.
        if (!l.resultat) return pastille(l.etape || 'en cours', 'var(--text2)', 'var(--neutre)');
        return l.resultat === 'CLEARED'
          ? pastille('CLEARED', '#16a34a', 'rgba(22,163,74,.16)')
          : pastille('FAILED', '#b91c1c', 'rgba(212,43,43,.16)');
      case 'typo':
        if (!l.typo) return '';
        return (
          <>
            {pastille(l.typo, 'var(--text2)', 'var(--neutre)')}
            {/* La typologie a change depuis l'intervention : le dire, plutot
                que de laisser croire que le CR portait celle-ci. */}
            {l.typo_archive && (
              <span title={`Le compte rendu portait « ${l.typo_archive} »`}
                style={{ display: 'block', fontSize: 10.5, color: 'var(--text3)', marginTop: 2 }}>
                CR : {l.typo_archive}
              </span>
            )}
          </>
        );
      case 'etape':
        return l[cle] ? pastille(l[cle], 'var(--text2)', 'var(--neutre)') : '';
      case 'date':
        return (
          <>
            <span style={{ whiteSpace: 'nowrap' }}>{jourFr(dateLigne(l)) || '—'}</span>
            {granularite === 'passage' && l.heure_intervention && c.champDate === 'intervention' && (
              <span style={{ fontSize: 11, color: 'var(--text3)' }}> {l.heure_intervention}</span>
            )}
          </>
        );
      case 'cause':
        return l.cause
          ? <span style={{ fontSize: 11.5, color: '#b45309' }}>{l.cause}</span>
          : '';
      case 'montant':
        return <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{euros(l.montant)}</span>;
      case 'fpv':
        // Le FPV n'est plus facture a prix fixe : la case suffit, le montant
        // n'apprenait rien.
        if (l.fpv) return pastille('FPV', '#7e22ce', 'rgba(168,85,247,.16)');
        // Coche au compte rendu puis decoche sur la fiche : c'est ce que la
        // conduite d'activite cherche quand elle recoupe un attachement.
        if (l.fpv_decoche) return pastille('décoché', '#b45309', 'rgba(217,119,6,.16)');
        return '';
      case 'passages':
        return <span style={{ fontWeight: 700 }}>{l.passages}</span>;
      case 'bilan':
        return (
          <span style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
            <span style={{ color: '#16a34a', fontWeight: 700 }}>{l.cleared}</span>
            {' / '}
            <span style={{ color: '#b91c1c', fontWeight: 700 }}>{l.failed}</span>
          </span>
        );
      default:
        return l[cle] == null ? '' : String(l[cle]);
    }
  };

  const champ = { width: '100%', fontSize: 12.5, padding: '8px 10px', borderRadius: 8,
    border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' };

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Recherche</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2,
                        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {data
              ? <span>{data.total_index.toLocaleString('fr-FR')} passages indexés sur nos
                  données locales — réponse immédiate, sans passer par Zoho</span>
              : <span>Construction de l’index…</span>}
            {data && data.zoho_releve && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7,
                             padding: '3px 9px', borderRadius: 20, background: 'var(--bg3)' }}>
                <span>Zoho indexé {ageTexte(data.zoho_releve.le)} ·{' '}
                  {(data.zoho_releve.total || 0).toLocaleString('fr-FR')} dossiers</span>
                <button onClick={rafraichirFpv} disabled={fpvEnCours}
                  style={{ font: 'inherit', fontSize: 11.5, fontWeight: 700, background: 'none',
                           border: 0, color: 'var(--blue2)', cursor: 'pointer', padding: 0 }}>
                  {fpvEnCours ? 'indexation…' : 'Réindexer'}
                </button>
              </span>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {enregistrees.map((r, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
              <button onClick={() => rappeler(r)}
                style={{ font: 'inherit', fontSize: 12, fontWeight: 600, padding: '5px 10px',
                  borderRadius: '20px 0 0 20px', cursor: 'pointer', background: 'var(--bg3)',
                  color: 'var(--text2)', border: '1px solid var(--border)', borderRight: 0 }}>
                {r.nom}
              </button>
              <button onClick={() => oublier(i)} aria-label={`Oublier « ${r.nom} »`}
                style={{ font: 'inherit', fontSize: 12, padding: '5px 8px', borderRadius: '0 20px 20px 0',
                  cursor: 'pointer', background: 'var(--bg3)', color: 'var(--text3)',
                  border: '1px solid var(--border)' }}>×</button>
            </span>
          ))}
          <button onClick={enregistrer}
            style={{ font: 'inherit', fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 20,
              cursor: 'pointer', background: 'transparent', color: 'var(--text3)',
              border: '1px dashed var(--border2)' }}>
            + enregistrer celle-ci
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', background: 'var(--bg)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 16, padding: '16px 20px 40px' }}>

          {/* ── Le rail des filtres ──────────────────────────────────────── */}
          <div style={{ flex: '0 0 288px', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>

            <Bloc titre="Texte — cherche partout" accent="var(--blue2)">
              <input style={champ} value={c.q} onChange={e => setC(x => ({ ...x, q: e.target.value }))}
                placeholder="Réf O ou W, client, technicien, ville, commentaire…" />
              <span style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.45 }}>
                Chaque mot doit se trouver. Les commentaires Covage de l’historique sont inclus.
                Sans texte, la recherche part quand même : ce sont les filtres qui la définissent.
              </span>
            </Bloc>

            <Bloc titre="Période" accent="var(--blue2)">
              <div>
                <label htmlFor="champ-date" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>
                  Sur quelle date ?
                </label>
                <select id="champ-date" style={champ} value={c.champDate}
                  onChange={e => setC(x => ({ ...x, champDate: e.target.value }))}>
                  {CHAMPS_DATE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label htmlFor="d1" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Du</label>
                  <input id="d1" type="date" style={champ} value={c.d1} onChange={e => setC(x => ({ ...x, d1: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="d2" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', display: 'block', marginBottom: 4 }}>Au</label>
                  <input id="d2" type="date" style={champ} value={c.d2} onChange={e => setC(x => ({ ...x, d2: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {RACCOURCIS.map(([nom, calc]) => (
                  <button key={nom} onClick={() => { const [a, b] = calc(); setC(x => ({ ...x, d1: a, d2: b })); }}
                    style={{ font: 'inherit', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                      background: 'var(--bg3)', color: 'var(--text2)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                    {nom}
                  </button>
                ))}
              </div>
            </Bloc>

            {/* Le FPV décide d'une ligne d'attachement : c'est lui qu'on
                recoupe avec ce que Covage renvoie chaque mois. */}
            {/* Le FPV vient de la FICHE, pas du compte rendu : le technicien
                coche, la conduite d'activite decoche quand la condition
                d'application n'est pas remplie, et c'est la fiche qui part en
                facturation. L'ecart entre les deux est lui-meme un filtre. */}
            <Bloc titre="FPV et attachement" accent="#a855f7">
              <Coche libelle="FPV coché sur la fiche" n={f.fpv}
                coche={c.fpv === 'true'} onChange={() => setC(x => ({ ...x, fpv: x.fpv === 'true' ? '' : 'true' }))} />
              <Coche libelle="Sans FPV sur la fiche"
                coche={c.fpv === 'non'} onChange={() => setC(x => ({ ...x, fpv: x.fpv === 'non' ? '' : 'non' }))} />
              <Coche libelle="Coché au compte rendu du technicien" n={f.fpv_cr}
                coche={c.fpv_cr} onChange={() => setC(x => ({ ...x, fpv_cr: !x.fpv_cr }))} />
              <Coche libelle="Décoché par la CA après le CR" n={f.fpv_decoche}
                coche={c.fpv_decoche} onChange={() => setC(x => ({ ...x, fpv_decoche: !x.fpv_decoche }))} />
              <Coche libelle="Avec un devis" n={f.devis}
                coche={c.avec_devis} onChange={() => setC(x => ({ ...x, avec_devis: !x.avec_devis }))} />
              <Coche libelle="Montant renseigné" n={f.montant}
                coche={c.avec_montant} onChange={() => setC(x => ({ ...x, avec_montant: !x.avec_montant }))} />
              <Coche libelle="Avec un DOSTEC" n={f.dostec}
                coche={c.avec_dostec} onChange={() => setC(x => ({ ...x, avec_dostec: !x.avec_dostec }))} />
              <Coche libelle="Sorti du calcul SLA" n={f.hors_sla}
                coche={c.hors_sla} onChange={() => setC(x => ({ ...x, hors_sla: !x.hors_sla }))} />
            </Bloc>

            <Facette titre="Résultat du compte rendu" accent="#16a34a"
              valeurs={[...(f.resultat || []), { valeur: 'AUCUN', n: null }].map(v =>
                v.valeur === 'AUCUN' ? { valeur: 'AUCUN', n: null, libelle: 'Sans compte rendu' } : v)}
              choisies={c.res} onBasculer={v => basculer('res', v)} />

            <Facette titre="Type de compte rendu" valeurs={f.type_cr || []}
              choisies={c.type} onBasculer={v => basculer('type', v)} />

            <Facette titre="Typologie de raccordement" valeurs={f.typo || []}
              choisies={c.typo} onBasculer={v => basculer('typo', v)} />

            <Facette titre="Technicien" valeurs={f.technicien || []}
              choisies={c.tech} onBasculer={v => basculer('tech', v)} max={10} />

            <Facette titre="Étape du dossier" valeurs={f.etape || []}
              choisies={c.etape} onBasculer={v => basculer('etape', v)} max={10} />

            <Facette titre="Cause d’échec" accent="#d97706" valeurs={f.cause || []}
              choisies={c.cause} onBasculer={v => basculer('cause', v)} max={10} />

            <Facette titre="Département" valeurs={f.departement || []}
              choisies={c.departement} onBasculer={v => basculer('departement', v)} max={10}
              vides="Aucun code postal connu sur ces lignes." />

            <Bloc titre="Passages sur le dossier">
              {[['1', 'Premier passage'], ['2', 'Deuxième passage'], ['3', 'Trois passages ou plus']].map(([v, l]) => (
                <Coche key={v} libelle={l} coche={c.passages.includes(v)} onChange={() => basculer('passages', v)} />
              ))}
            </Bloc>
          </div>

          {/* ── Les résultats ───────────────────────────────────────────── */}
          <div style={{ flex: '999 1 620px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {/* Le compte suit la granularite : il donnait le nombre de
                  passages dans les deux vues. */}
              <span style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-.02em' }}>
                {chargement ? '…' : (t.resultats != null ? t.resultats : t.lignes || 0).toLocaleString('fr-FR')}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                {granularite === 'dossier' ? 'dossier' : 'passage'}{(t.resultats || 0) > 1 ? 's' : ''}
                {granularite === 'dossier' && t.lignes ? <> · {t.lignes} passages retenus</> : null}
                {(c.d1 || c.d2) && <> · sur la {(CHAMPS_DATE.find(x => x[0] === c.champDate) || [])[1].toLowerCase()}</>}
              </span>

              {/* Le sélecteur de granularité : un W est un passage, une réf O
                  est un dossier, et les deux lectures servent. */}
              <div style={{ display: 'flex', background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 8, padding: 2 }}>
                {[['passage', 'Un par passage'], ['dossier', 'Un par dossier']].map(([v, l]) => (
                  <button key={v} onClick={() => { setGranularite(v); setTri({ cle: '', sens: 'desc' }); }}
                    style={{ font: 'inherit', fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: 6,
                      border: 0, cursor: 'pointer',
                      background: granularite === v ? 'rgba(37,99,235,.14)' : 'transparent',
                      color: granularite === v ? 'var(--blue2)' : 'var(--text3)' }}>{l}</button>
                ))}
              </div>

              <span style={{ marginLeft: 'auto', display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={exporter} disabled={exportEnCours || !t.lignes}>
                  {exportEnCours ? '⏳ Export…' : '⬇ Exporter en CSV'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setC(VIDE); setTri({ cle: '', sens: 'desc' }); }}>
                  Tout effacer
                </button>
              </span>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {tags.length === 0 && (
                <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>
                  Aucun filtre — tout le parc est affiché, du plus récent au plus ancien.
                </span>
              )}
              {tags.map(([libelle, retirer], i) => (
                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5,
                  fontWeight: 600, padding: '3px 7px 3px 9px', borderRadius: 6,
                  background: 'rgba(37,99,235,.13)', color: 'var(--blue2)', border: '1px solid rgba(37,99,235,.3)' }}>
                  {libelle}
                  <button onClick={retirer} aria-label="Retirer ce filtre"
                    style={{ font: 'inherit', background: 'none', border: 0, color: 'inherit', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>

            {erreur && (
              <div style={{ padding: '10px 13px', borderRadius: 8, fontSize: 13,
                background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>{erreur}</div>
            )}

            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 900 }}>
                  <thead>
                    <tr>
                      {colonnes.map(([cle, nom]) => {
                        const triable = !!champTri[cle];
                        const actif = tri.cle && tri.cle === champTri[cle];
                        return (
                          <th key={cle}
                            onClick={() => triable && setTri(x => ({
                              cle: champTri[cle],
                              sens: x.cle === champTri[cle] && x.sens === 'desc' ? 'asc' : 'desc',
                            }))}
                            style={{ textAlign: 'left', padding: '9px 11px', fontSize: 10.5, fontWeight: 800,
                              letterSpacing: '.05em', textTransform: 'uppercase',
                              color: actif ? 'var(--blue2)' : 'var(--text3)', background: 'var(--bg3)',
                              whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)',
                              cursor: triable ? 'pointer' : 'default', userSelect: 'none' }}>
                            {nom}{actif ? (tri.sens === 'asc' ? ' ▲' : ' ▼') : ''}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.lignes || []).map((l, i) => (
                      <tr key={(l.dossier_id || '') + '|' + (l.ref_work_order || '') + i}
                        style={{ borderBottom: '1px solid var(--border)' }}>
                        {colonnes.map(([cle]) => (
                          <td key={cle} style={{ padding: '9px 11px', verticalAlign: 'top' }}>{cellule(l, cle)}</td>
                        ))}
                      </tr>
                    ))}
                    {!chargement && data && data.lignes.length === 0 && (
                      <tr><td colSpan={colonnes.length}>
                        <div style={{ padding: '34px 18px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                          Aucun résultat avec ces filtres. Retirez-en un au-dessus.
                        </div>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Une recherche répond aussi « combien ». */}
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: '11px 13px',
                background: 'var(--bg3)', borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text2)' }}>
                <span><strong style={{ color: 'var(--text)' }}>{(t.lignes || 0).toLocaleString('fr-FR')}</strong> passages</span>
                <span><strong style={{ color: 'var(--text)' }}>{(t.dossiers || 0).toLocaleString('fr-FR')}</strong> dossiers distincts</span>
                <span>
                  <strong style={{ color: '#16a34a' }}>{t.cleared || 0}</strong> CLEARED ·{' '}
                  <strong style={{ color: '#b91c1c' }}>{t.failed || 0}</strong> FAILED
                  {t.taux != null && <> · taux <strong style={{ color: 'var(--text)' }}>{t.taux} %</strong></>}
                </span>
                {/* Un FPV s'attache a la commande : deux passages d'une meme
                    commande ne font pas deux lignes d'attachement. */}
                <span>
                  <strong style={{ color: '#7e22ce' }}>{t.fpv_dossiers || 0}</strong> FPV
                  {t.fpv_decoche ? <span style={{ color: '#b45309' }}> · {t.fpv_decoche} décoché{t.fpv_decoche > 1 ? 's' : ''} par la CA</span> : null}
                </span>
                <span>devis cumulés : <strong style={{ color: 'var(--text)' }}>{euros(t.montant) || '0 €'}</strong></span>
                {data?.tronque && (
                  <span style={{ color: '#b45309' }}>
                    affichage limité à 500 lignes — l’export CSV porte tout
                  </span>
                )}
              </div>
            </div>

            <div style={{ fontSize: 11.5, color: 'var(--text3)', lineHeight: 1.65, maxWidth: '104ch' }}>
              <strong style={{ color: 'var(--text2)' }}>L’unité est le work order.</strong>{' '}
              Une commande revient sous la même référence O avec un W neuf : c’est le W qui est un passage.
              « Un par dossier » regroupe les passages retenus et cumule leurs résultats, leurs FPV et leurs devis ;
              un dossier sort dès qu’un seul de ses passages répond aux filtres.
              <br /><br />
              <strong style={{ color: 'var(--text2)' }}>Typologie, étape, client et FPV viennent de Zoho.</strong>{' '}
              Nos archives — l’historique des étapes et l’index des comptes rendus — gardent ce qui était
              vrai au moment de l’intervention, ce qu’on attend d’une archive. Mais une typologie corrigée
              dans Zoho doit se voir tout de suite. L’état courant est donc relevé, et la mise à jour ne
              rapporte que ce qui a changé depuis la dernière recherche : une correction est visible en
              quelques secondes. Quand la typologie du dossier diffère de celle du compte rendu, les deux
              s’affichent.
              <br /><br />
              <strong style={{ color: 'var(--text2)' }}>Le résultat vient de l’étape.</strong>{' '}
              Un compte rendu se rattache par sa référence W, et quand celle-ci diffère de celle de
              l’historique le rattachement échoue : la ligne annonçait « sans CR » là où la fiche en
              montrait un. C’est donc l’étape — celle que Zoho porte et que la page Dossiers affiche —
              qui donne l’issue. Le verdict du compte rendu reste dans l’export, à côté, pour qu’un
              écart entre les deux se voie.
              <br /><br />
              <strong style={{ color: 'var(--text2)' }}>Sur quelle date ?</strong>{' '}
              « Les CLEARED de septembre » n’a pas la même réponse selon qu’on parle de la date d’intervention,
              de celle du compte rendu ou de la clôture. L’ancienne page décidait à votre place —
              la date d’intervention, en dur.
              <br /><br />
              <strong style={{ color: 'var(--text2)' }}>Le FPV est celui de la fiche.</strong>{' '}
              Le technicien coche la case dans son compte rendu, la conduite d’activité la décoche sur le
              dossier quand la condition d’application n’est pas remplie, et c’est la fiche qui part en
              facturation. En septembre 2026, 77 comptes rendus portaient la case et 64 dossiers la
              gardaient. Les deux valeurs sont là, et l’écart est un filtre à lui seul.
              <br /><br />
              <strong style={{ color: 'var(--text2)' }}>L’export porte tout ce que la recherche a trouvé</strong>{' '}
              — pas les 500 lignes affichées — en point-virgule et UTF-8 avec BOM, qu’Excel ouvre en colonnes
              sans manipulation. Vingt-et-une colonnes par passage, dix-neuf par dossier.
              {data?.index_construit_le && (
                <>
                  <br /><br />
                  Index de {data.total_index.toLocaleString('fr-FR')} passages, construit le{' '}
                  {new Date(data.index_construit_le).toLocaleString('fr-FR')} et reconstruit dès qu’un
                  compte rendu ou un import le périme.
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
